<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import FormSeparator from '@js/components/forms/FormSeparator.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { array, boolean, object, string } from 'yup';

const props = defineProps({
    quotation: {
        required: false,
        type: Object,
        default: null,
    },
    users: {
        required: true,
        type: Array,
        default: () => [],
    },
});

const { route } = useRoute();

const statuses = [
    { value: 'ongoing', label: 'En cours' },
    { value: 'won', label: 'Gagné' },
    { value: 'to_invoice', label: 'À facturer' },
    { value: 'invoiced', label: 'Facturé' },
    { value: 'partially_invoiced', label: 'Partiellement facturé' },
    { value: 'lost', label: 'Perdu' },
];

const form = reactive({
    status: props.quotation
        ? statuses.find((status) => status.value === props.quotation.status)
        : statuses[0],
    date: props.quotation ? props.quotation.date : null,
    customer: props.quotation ? props.quotation.customer : null,
    type: props.quotation ? props.quotation.type : null,
    link: props.quotation ? props.quotation.link : null,
    signedLink: props.quotation ? props.quotation.signedLink : null,
    toCall: props.quotation ? props.quotation.toCall : false,
    comment: props.quotation ? props.quotation.comment : null,
    sentAt: props.quotation ? props.quotation.sentAt : null,
    calledAt: props.quotation ? props.quotation.calledAt : null,
    users: props.quotation
        ? props.users.filter((user) =>
              props.quotation.userIds.includes(user.id),
          )
        : [],
});

const isSubmitting = ref(false);

const validationSchema = object({
    status: object().required('Ce champ est obligatoire.'),
    date: string().required('Ce champ est obligatoire.'),
    customer: string().required('Ce champ est obligatoire.'),
    type: string().required('Ce champ est obligatoire.'),
    link: string().url('Veuillez saisir une URL valide.').nullable(),
    signedLink: string().url('Veuillez saisir une URL valide.').nullable(),
    toCall: boolean().required('Ce champ est obligatoire.'),
    comment: string().nullable(),
    sentAt: string().nullable(),
    calledAt: string().nullable(),
    users: array().min(1, 'Ce champ est obligatoire.'),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            props.quotation
                ? route('front.projectManagement.apikom.postQuotation', {
                      id: props.quotation.id,
                  })
                : route('front.projectManagement.apikom.postQuotation'),
            {
                status: form.status.value,
                date: form.date,
                customer: form.customer,
                type: form.type,
                link: form.link,
                signedLink: form.signedLink,
                toCall: form.toCall,
                comment: form.comment,
                sentAt: form.sentAt,
                calledAt: form.calledAt,
                userIds: form.users.map((user) => user.id),
            },
        )
        .then(() => {
            toastSaved();

            router.visit(route('front.projectManagement.apikom.quotations'));
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);
            router.visit(route('front.projectManagement.apikom.quotations'));
        });
}
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
    >
        <FormColumn>
            <FormContent>
                <FormGrid :columns="3">
                    <ListboxField
                        v-model="form.status"
                        field-id="status"
                        field-name="status"
                        label="Statut"
                        :options="statuses"
                        required
                    >
                        <template #buttonContent>
                            {{ form.status.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>

                    <InputField
                        v-model="form.type"
                        label="Type"
                        field-name="type"
                        field-id="type"
                        required
                    />

                    <InputField
                        v-model="form.customer"
                        label="Client"
                        field-name="customer"
                        field-id="customer"
                        required
                    />
                </FormGrid>

                <FormSeparator />

                <FormGrid>
                    <ComboboxField
                        v-model="form.users"
                        :options="users"
                        label="Responsable"
                        field-name="users"
                        field-id="users"
                        filteredOptionKey="fullName"
                        multiple
                        required
                    >
                        <template #optionContent="{ option }">
                            {{ option.fullName }}
                        </template>
                    </ComboboxField>
                </FormGrid>

                <FormSeparator />

                <FormGrid :columns="2">
                    <InputField
                        v-model="form.date"
                        label="Date"
                        field-name="date"
                        field-id="date"
                        type="date"
                        required
                    />

                    <InputField
                        v-model="form.sentAt"
                        label="Envoyé le"
                        field-name="sentAt"
                        field-id="sentAt"
                        type="date"
                    />
                </FormGrid>
            </FormContent>

            <FormContent>
                <SwitchField
                    v-model="form.toCall"
                    label="À relancer"
                    field-name="toCall"
                    field-id="toCall"
                />

                <FormGrid>
                    <InputField
                        v-model="form.calledAt"
                        label="Dernière relance"
                        field-name="calledAt"
                        field-id="calledAt"
                        type="date"
                    />
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid :columns="2">
                    <InputField
                        v-model="form.link"
                        label="Lien devis"
                        field-name="link"
                        field-id="link"
                        type="url"
                    />

                    <InputField
                        v-model="form.signedLink"
                        label="Lien devis signé"
                        field-name="signedLink"
                        field-id="signedLink"
                        type="url"
                    />
                </FormGrid>
            </FormContent>

            <FormContent>
                <EditorField
                    v-model="form.comment"
                    label="Commentaire"
                    field-name="comment"
                    field-id="comment"
                />
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="props.quotation"
                    :url="
                        route(
                            'front.projectManagement.apikom.deleteQuotation',
                            {
                                quotation: props.quotation.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.apikom.quotations',
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
